import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { CheckCircle, Spinner, WarningCircle, ArrowRight, Envelope } from "@phosphor-icons/react";

/**
 * /welcome — Post-checkout landing page.
 *
 * Contract:
 *  - Never activates entitlements from client-side state.
 *  - Polls GET /api/subscription/status every 3s (up to 90s).
 *  - Considers the checkout confirmed only when the server reports a Paddle
 *    subscription_id AND subscription_status ∈ {active, trialing}.
 *  - Displays the plan label, billing interval and renewal date only after
 *    the webhook-backed activation is confirmed.
 */
export default function Welcome() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const txId = params.get("tx") || params.get("_ptxn") || "";

  const [phase, setPhase] = useState("verifying"); // verifying | confirmed | timeout | error
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  const MAX_ATTEMPTS = 30;   // 30 × 3s = 90s
  const POLL_INTERVAL = 3000;

  useEffect(() => {
    if (!user) {
      navigate(`/login?next=/welcome${txId ? `?tx=${encodeURIComponent(txId)}` : ""}`);
      return;
    }
    let cancelled = false;

    async function poll() {
      try {
        const r = await api.get("/subscription/status");
        if (cancelled) return;
        setStatus(r.data);
        const p = r.data?.paddle || {};
        const activated = !!p.subscription_id && ["active", "trialing"].includes((r.data?.status || "").toLowerCase());
        if (activated) {
          setPhase("confirmed");
          if (timerRef.current) window.clearInterval(timerRef.current);
          return;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[welcome] status poll failed:", e?.message || e);
      }
      setAttempts((a) => {
        const next = a + 1;
        if (next >= MAX_ATTEMPTS) {
          setPhase("timeout");
          if (timerRef.current) window.clearInterval(timerRef.current);
        }
        return next;
      });
    }

    poll(); // fire immediately
    timerRef.current = window.setInterval(poll, POLL_INTERVAL);
    return () => {
      cancelled = true;
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, txId]);

  const paddle = status?.paddle || {};
  const planLabel = status?.plan_label || (status?.plan_key ? status.plan_key : "");

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="welcome-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/dashboard" testId="welcome-logo" />
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground font-medium" data-testid="welcome-dashboard-link">
            Go to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {phase === "verifying" && (
          <div className="text-center" data-testid="welcome-verifying">
            <div className="inline-flex h-14 w-14 rounded-full bg-primary/10 items-center justify-center mb-6 animate-pulse">
              <Spinner size={26} className="text-primary animate-spin" weight="bold" />
            </div>
            <div className="text-overline text-primary mb-3">Verification pending</div>
            <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-[1.05]">Finalising your subscription…</h1>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              We&apos;re waiting for Paddle to confirm your payment. Access will unlock automatically as soon as
              the webhook is verified server-side. This usually takes a few seconds.
            </p>
            <p className="mt-6 text-xs text-muted-foreground" data-testid="welcome-attempts">
              Check {attempts + 1} / {MAX_ATTEMPTS}
            </p>
            {txId && (
              <p className="mt-2 text-[10px] font-mono text-muted-foreground">Reference: {txId}</p>
            )}
          </div>
        )}

        {phase === "confirmed" && (
          <div data-testid="welcome-confirmed">
            <div className="text-center">
              <div className="inline-flex h-14 w-14 rounded-full bg-teal-soft items-center justify-center mb-6">
                <CheckCircle size={30} weight="fill" className="text-teal" />
              </div>
              <div className="text-overline text-teal mb-3">Subscription active</div>
              <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-[1.05]">
                Welcome to Orixa <span className="capitalize">{planLabel}</span>.
              </h1>
              <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                Your access is live. All plan features are now unlocked for your organisation.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard label="Plan"      value={`Orixa ${planLabel || "—"}`} testid="welcome-plan" />
              <InfoCard label="Billing"   value={(paddle.billing_interval || "").toLowerCase() === "year" ? "Annual" : "Monthly"} testid="welcome-interval" />
              <InfoCard label="Status"    value={(status?.status || "active").toString()} testid="welcome-status" />
              <InfoCard label="Renews"    value={fmtDate(paddle.billing_period_end)} testid="welcome-renews" />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/dashboard" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:opacity-90 card-elev" data-testid="welcome-cta-dashboard">
                Enter dashboard <ArrowRight weight="bold" />
              </Link>
              <Link to="/billing" className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-6 py-3 text-sm font-semibold hover:bg-secondary" data-testid="welcome-cta-billing">
                Manage subscription
              </Link>
              <a href="mailto:support@orixa-invest.co" className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground px-4 py-3" data-testid="welcome-cta-support">
                <Envelope size={14} weight="bold" /> support@orixa-invest.co
              </a>
            </div>
          </div>
        )}

        {phase === "timeout" && (
          <div className="text-center" data-testid="welcome-timeout">
            <div className="inline-flex h-14 w-14 rounded-full bg-amber-100 items-center justify-center mb-6">
              <WarningCircle size={28} weight="fill" className="text-amber-600" />
            </div>
            <div className="text-overline text-amber-700 mb-3">Still verifying</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.1]">Almost there.</h1>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              Payment providers can occasionally take a minute or two to notify us. Your subscription will
              activate automatically as soon as the Paddle webhook is verified — we&apos;ll email you the confirmation.
              You can safely leave this page.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:opacity-90" data-testid="welcome-timeout-dashboard">
                Go to dashboard <ArrowRight weight="bold" />
              </Link>
              <a href="mailto:support@orixa-invest.co" className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-6 py-3 text-sm font-semibold hover:bg-secondary" data-testid="welcome-timeout-support">
                Contact support
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function InfoCard({ label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={testid}>
      <div className="text-overline text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-xl tracking-tight capitalize">{value || "—"}</div>
    </div>
  );
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}
